import type { Nullable } from "@msuite/utils/types";
import { useId, useLayoutEffect } from "react";
import { useBreadcrumbsStore } from "./breadcrumbs-store";

/**
 * Name a URL segment in the breadcrumbs for as long as the calling component is mounted — the
 * page of an entity calls it with the entity's id and the name a person recognises it by, and its
 * crumb shows that name instead of the id. Nothing is registered while either value is missing, so
 * a page can call it before its data has loaded.
 *
 * A layout effect rather than an effect, so a name already known on the first render is in place
 * before the first paint instead of replacing the id a frame later.
 *
 * @param segment - The URL segment to name, usually the id the route carries.
 * @param title - The name the crumb shows.
 */
export function useBreadcrumbTitle(segment: Nullable<string>, title: Nullable<string>) {
	/** Hooks */
	const registrationId = useId();
	const registerTitle = useBreadcrumbsStore((store) => store.registerTitle);
	const unregisterTitle = useBreadcrumbsStore((store) => store.unregisterTitle);

	/** Effects */
	useLayoutEffect(() => {
		if (!segment || !title) return;
		registerTitle(registrationId, { segment, title });
		return () => unregisterTitle(registrationId);
	}, [segment, title, registrationId]);
}
